import React, { useState } from "react";
import { useModal } from "../../components/modal";
import { Button } from "@mui/material";
import {
  ModalButtonContainer,
  NegativeModalButton,
  PositiveModalButton,
} from "../../components/Button";
import ImageUploader from "../../components/uploader/imageUploader";
import { CustomTextField } from "../../components/TextFields";
import { SelectInput } from "../../components/inputs/SelectInput";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Banner, BannerLocation } from "../../types/banner";
import {
  useAddBannerMutation,
  useUpdateBannerMutation,
} from "../../app/services/banners.api";
import getImageLink from "../../utils/getImageLink";
import LoadingButton from "../../components/Button/LoadingButton";

const schema = yup.object().shape({
  title: yup.string().required("عنوان الزامی است."),
  link: yup.string().required("لینک الزامی است."),
  location: yup
    .number()
    .default(BannerLocation.SEGMENT_1)
    .required("بنر الزامی است."),
  desktop: yup.string().required(),
  mobile: yup.string().required(),
});

export default function AddModal({
  defaultValue,
}: {
  defaultValue?: Partial<Banner>;
}) {
  const { setShow } = useModal();
  const [desktop, setDesktop] = useState<File | null>();
  const [mobile, setMobile] = useState<File | null>();
  const [addBanner, addState] = useAddBannerMutation();
  const [updateBanner, updateState] = useUpdateBannerMutation();
  const {
    setValue,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValue
      ? {
          ...defaultValue,
          desktop: defaultValue?.desktop
            ? getImageLink(defaultValue.desktop)
            : undefined,
          mobile: defaultValue?.mobile
            ? getImageLink(defaultValue.mobile)
            : undefined,
        }
      : {},
  });

  const isEdit = defaultValue?._id ? true : false;

  return (
    <React.Fragment>
      <ImageUploader
        required
        title="تصویر"
        defaultValue={getValues("desktop")}
        onChange={(file) => {
          const fileName = file?.name;
          setValue("desktop", fileName ?? "");
          setDesktop(file);
        }}
      />
      <ImageUploader
        required
        title="بنر موبایل"
        defaultValue={getValues("mobile")}
        onChange={(file) => {
          const fileName = file?.name;
          setValue("mobile", fileName ?? "");
          setMobile(file);
        }}
      />
      <CustomTextField
        variant="outlined"
        placeholder="عنوان را وارد کنید."
        label="عنوان"
        required
        defaultValue={getValues("title")}
        onChange={(e) => setValue("title", e.target.value)}
        error={!!errors.title}
        helperText={errors.title?.message}
      />
      <CustomTextField
        variant="outlined"
        placeholder="لینک را وارد کنید."
        label="لینک"
        required
        onChange={(e) => setValue("link", e.target.value)}
        defaultValue={getValues("link")}
        error={!!errors.link}
        helperText={errors.link?.message}
      />
      <SelectInput
        label="صفحه بنر"
        error={!!errors.location}
        helperText={errors.location?.message}
        onChange={(value: { label: string; value?: BannerLocation } | null) =>
          setValue("location", value?.value as BannerLocation)
        }
        defaultValue={{
          label:
            getValues("location") === BannerLocation.SEGMENT_1
              ? "Segment_1"
              : "Segment_2",
          value: getValues("location"),
        }}
        required
        options={[
          { label: "Segment_1", value: BannerLocation.SEGMENT_1 },
          { label: "Segment_2", value: BannerLocation.SEGMENT_2 },
        ]}
      />
      <ModalButtonContainer>
        <Button
          style={NegativeModalButton}
          sx={{
            color: "divider",
            bgcolor: "primary.main",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "divider",
            ":hover": {
              bgcolor: "divider",
              color: "primary.main",
            },
          }}
          onClick={() => setShow(false)}
        >
          انصراف
        </Button>
        <LoadingButton
          style={PositiveModalButton}
          sx={{
            ":hover": {
              bgcolor: "customColors.purple",
              color: "secondary.main",
            },
          }}
          isLoading={addState.isLoading || updateState.isLoading}
          onClick={handleSubmit(
            (data) => {
              if (isEdit) {
                const formData = new FormData();
                formData.append("title", data.title);
                formData.append("link", data.link);
                //@ts-ignore
                formData.append("location", data.location);
                //@ts-ignore
                if (desktop) formData.append("desktop", desktop);
                //@ts-ignore
                if (mobile) formData.append("mobile", mobile);
                updateBanner({
                  bannerId: defaultValue?._id as string,
                  data: formData,
                })
                  .unwrap()
                  .then(() => setShow(false))
                  .catch((err) => {});
                return;
              }

              const formData = new FormData();
              formData.append("title", data.title);
              formData.append("link", data.link);
              //@ts-ignore
              formData.append("location", data.location);
              //@ts-ignore
              formData.append("desktop", desktop);
              //@ts-ignore
              formData.append("mobile", mobile);

              addBanner(formData)
                .unwrap()
                .then(() => setShow(false))
                .catch((err) => {});
            },
            (err) => console.error(err)
          )}
        >
          تایید
        </LoadingButton>
      </ModalButtonContainer>
    </React.Fragment>
  );
}
